/**
 * Pilot: deep links for Kaching quantity-break offers (?offer=1|2|3|4).
 * Scoped via window.enzoOfferDeepLink config from main-product.liquid.
 */
(function () {
  'use strict';

  const config = window.enzoOfferDeepLink;
  if (!config || !config.productId) return;

  const OFFER_PARAM = 'offer';
  const PRODUCT_ID = String(config.productId);
  let dealBars = null;
  let urlSyncDisabled = false;
  let mirrorObserver = null;
  let mirroringHeaderPrice = false;
  let mirrorDebounceTimer = null;
  let settingsPatched = false;
  let offerFromUrlApplied = false;
  let kachingReadyHandled = false;

  function getProductScope() {
    return (
      document.querySelector(`#MainProduct-${config.sectionId}`) ||
      document.querySelector('.main-pro') ||
      document
    );
  }

  function collectRoots() {
    const roots = [document];
    document.querySelectorAll('kaching-bundles-block, kaching-bundles-bar, kaching-bundle').forEach((el) => {
      if (el.shadowRoot) roots.push(el.shadowRoot);
    });
    return roots;
  }

  function queryAll(selector) {
    const nodes = [];
    collectRoots().forEach((root) => {
      root.querySelectorAll(selector).forEach((node) => nodes.push(node));
    });
    return nodes;
  }

  function getOfferFromUrl() {
    const raw = new URLSearchParams(window.location.search).get(OFFER_PARAM);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isInteger(n) && n >= 1 && n <= 4 ? n : null;
  }

  function getDealBlockSettingsEl() {
    return document.querySelector(
      `script.kaching-bundles-deal-block-settings[data-product-id="${PRODUCT_ID}"]`
    );
  }

  function loadDealBars() {
    if (dealBars) return dealBars;
    const el = getDealBlockSettingsEl();
    if (!el) return null;
    try {
      const settings = JSON.parse(el.textContent);
      dealBars = (settings.dealBars || [])
        .filter((bar) => !bar.dealBarType || bar.dealBarType === 'quantity-break')
        .slice()
        .sort((a, b) => (a.quantity || 0) - (b.quantity || 0));
      return dealBars;
    } catch (err) {
      console.warn('[enzo-offer-deep-link] Could not parse Kaching deal block settings', err);
      return null;
    }
  }

  function dealBarIdForOffer(offer) {
    const bars = loadDealBars();
    return bars && bars[offer - 1] ? bars[offer - 1].id : null;
  }

  function offerForDealBarId(dealBarId) {
    const bars = loadDealBars();
    if (!bars || !dealBarId) return null;
    const idx = bars.findIndex((bar) => bar.id === dealBarId);
    return idx >= 0 ? idx + 1 : null;
  }

  function setOfferInUrl(offer) {
    if (urlSyncDisabled) return;
    const url = new URL(window.location.href);
    const next = String(offer);
    if (url.searchParams.get(OFFER_PARAM) === next) return;
    url.searchParams.set(OFFER_PARAM, next);
    window.history.replaceState({}, '', url.toString());
  }

  function markNativePriceElements() {
    const scope = getProductScope();
    scope.querySelectorAll('.price__sale .price-item--sale').forEach((el) => {
      if (el.closest('.product-content-mobile-show')) return;
      el.setAttribute('data-kaching-price', '');
    });
    scope.querySelectorAll('.price__sale s.price-item--regular').forEach((el) => {
      if (el.closest('.product-content-mobile-show')) return;
      el.setAttribute('data-kaching-price-compare', '');
    });
  }

  function isMobileHeaderPrice(el) {
    return Boolean(el.closest('.product-content-mobile-show'));
  }

  function normalizeKachingDisplayPrice(text) {
    if (!text) return text;
    return text
      .replace(/\s*\/?\s*Item\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Mobile header: same as desktop but tighter number formatting for the flex row. */
  function normalizeMobileDisplayPrice(text) {
    return normalizeKachingDisplayPrice(text).replace(/(\d)\.00(\D|$)/g, '$1$2');
  }

  function headerPriceText(el, saleOrCompareText) {
    return isMobileHeaderPrice(el)
      ? normalizeMobileDisplayPrice(saleOrCompareText)
      : normalizeKachingDisplayPrice(saleOrCompareText);
  }

  function stripItemSuffixFromHeaderPrices() {
    if (mirroringHeaderPrice) return;
    const scope = getProductScope();
    scope
      .querySelectorAll(
        '.price__sale .price-item--sale, .price-item--sale.intelligent-price, [data-kaching-price]'
      )
      .forEach((el) => {
        const raw = el.textContent.replace(/\s+/g, ' ').trim();
        if (!/\/?\s*Item\s*$/i.test(raw)) return;
        const next = headerPriceText(el, raw);
        if (next === raw) return;
        mirroringHeaderPrice = true;
        el.textContent = next;
        window.setTimeout(() => {
          mirroringHeaderPrice = false;
        }, 0);
      });
  }

  let priceSanitizeDebounce = null;
  function scheduleStripItemSuffix() {
    window.clearTimeout(priceSanitizeDebounce);
    priceSanitizeDebounce = window.setTimeout(stripItemSuffixFromHeaderPrices, 25);
  }

  let priceSanitizeObserver = null;
  function startPriceItemSanitizerObserver() {
    if (priceSanitizeObserver) return;
    const scope = getProductScope();
    priceSanitizeObserver = new MutationObserver((mutations) => {
      if (mirroringHeaderPrice) return;
      for (const mutation of mutations) {
        const node = mutation.target;
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        if (!(el instanceof Element)) continue;
        if (
          el.matches('[data-kaching-price], .price-item--sale, .intelligent-price') ||
          el.closest('.price__sale, .product-content-mobile-show .price')
        ) {
          scheduleStripItemSuffix();
          return;
        }
      }
    });
    priceSanitizeObserver.observe(scope, {
      subtree: true,
      characterData: true,
      childList: true,
    });
  }

  function getSelectedDealBarId() {
    const checked = queryAll('input[type="radio"][name^="kaching-bundles-deal-"]:checked')[0];
    if (!checked) return null;
    const bar = checked.closest('[data-deal-bar-id]');
    return bar ? bar.getAttribute('data-deal-bar-id') : null;
  }

  function readPricesFromDealBar(dealBarId) {
    if (!dealBarId) return null;

    const barHosts = queryAll(`[data-deal-bar-id="${dealBarId}"]`);
    for (const host of barHosts) {
      const roots = [host, host.shadowRoot].filter(Boolean);
      for (const root of roots) {
        const saleEl =
          root.querySelector('.kaching-bundles__bar-unit-price') ||
          root.querySelector('.kaching-bundles__bar-price');
        const compareEl = root.querySelector('.kaching-bundles__bar-full-price');
        if (!saleEl) continue;

        const saleText = normalizeKachingDisplayPrice(
          saleEl.textContent.replace(/\s+/g, ' ').trim()
        );
        const compareText = compareEl
          ? normalizeKachingDisplayPrice(
              compareEl.textContent.replace(/\s+/g, ' ').trim()
            )
          : null;

        if (saleText) return { saleText, compareText };
      }
    }
    return null;
  }

  function applyHeaderPrices(priceInfo) {
    if (!priceInfo || !priceInfo.saleText) return;

    const scope = getProductScope();
    const saleNodes = scope.querySelectorAll(
      '.price__sale .price-item--sale, .price-item--sale.intelligent-price'
    );

    mirroringHeaderPrice = true;
    try {
      saleNodes.forEach((el) => {
        el.textContent = headerPriceText(el, priceInfo.saleText);
      });

      if (priceInfo.compareText) {
        scope.querySelectorAll('.price__sale s.price-item--regular').forEach((el) => {
          el.textContent = headerPriceText(el, priceInfo.compareText);
        });
      }

      scope.querySelectorAll('.price').forEach((el) => {
        if (isMobileHeaderPrice(el)) return;
        if (priceInfo.compareText) el.classList.add('price--on-sale');
      });
    } finally {
      window.setTimeout(() => {
        mirroringHeaderPrice = false;
      }, 0);
    }
  }

  /** Copy per-item bundle price from selected Kaching bar into theme header price. */
  function mirrorHeaderPriceFromKaching() {
    const dealBarId = getSelectedDealBarId();
    const priceInfo = readPricesFromDealBar(dealBarId);
    if (priceInfo) applyHeaderPrices(priceInfo);
    markNativePriceElements();
    stripItemSuffixFromHeaderPrices();
  }

  function scheduleHeaderPriceMirror() {
    [0, 200, 800].forEach((delay) => {
      window.setTimeout(mirrorHeaderPriceFromKaching, delay);
    });
  }

  function scheduleHeaderPriceMirrorDebounced() {
    window.clearTimeout(mirrorDebounceTimer);
    mirrorDebounceTimer = window.setTimeout(scheduleHeaderPriceMirror, 40);
  }

  function startHeaderPriceMirrorObserver() {
    if (mirrorObserver) return;
    const scope = getProductScope();
    mirrorObserver = new MutationObserver((mutations) => {
      if (mirroringHeaderPrice) return;
      const fromKaching = mutations.some((mutation) => {
        const node = mutation.target;
        const el =
          node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        if (!(el instanceof Element)) return false;
        return (
          el.closest('kaching-bundles-block, kaching-bundles-bar, kaching-bundle') != null
        );
      });
      if (!fromKaching) return;
      scheduleHeaderPriceMirrorDebounced();
    });
    document.querySelectorAll('kaching-bundles-block, kaching-bundles-bar, kaching-bundle').forEach((el) => {
      mirrorObserver.observe(el, { childList: true, subtree: true, characterData: true });
    });
    mirrorObserver.observe(scope, {
      childList: true,
      subtree: false,
    });
  }

  function syncKachingNativePriceAfterVariantChange() {
    markNativePriceElements();

    const variantInput = document.querySelector(
      'form[data-type="add-to-cart-form"] input[name="id"]:not([disabled])'
    );
    if (variantInput) {
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const dealBarId = getSelectedDealBarId();
    if (dealBarId) selectDealBarInDom(dealBarId, true);

    scheduleHeaderPriceMirror();
  }

  function scheduleNativePriceSyncAfterVariantChange() {
    [0, 150, 500].forEach((delay) => {
      window.setTimeout(syncKachingNativePriceAfterVariantChange, delay);
    });
  }

  function patchKachingDealBlockSettings(offer) {
    if (settingsPatched) return;
    const el = getDealBlockSettingsEl();
    if (!el) return;

    try {
      const settings = JSON.parse(el.textContent);
      let changed = false;

      if (!settings.updateNativePrice) {
        settings.updateNativePrice = true;
        changed = true;
      }

      if (settings.updateNativePriceType !== 'item') {
        settings.updateNativePriceType = 'item';
        changed = true;
      }

      if (offer) {
        const dealBarId = dealBarIdForOffer(offer);
        if (dealBarId && settings.preselectedDealBarId !== dealBarId) {
          settings.preselectedDealBarId = dealBarId;
          changed = true;
        }
      }

      if (changed) el.textContent = JSON.stringify(settings);
      settingsPatched = true;
    } catch (err) {
      console.warn('[enzo-offer-deep-link] Could not patch Kaching settings', err);
    }
  }

  function findDealBarRadio(dealBarId) {
    const hosts = queryAll(`[data-deal-bar-id="${dealBarId}"]`);
    for (const host of hosts) {
      const roots = [host, host.shadowRoot].filter(Boolean);
      for (const root of roots) {
        const radio = root.querySelector('input[type="radio"]');
        if (radio) return radio;
      }
    }
    return null;
  }

  function selectDealBarInDom(dealBarId, force) {
    if (!dealBarId) return false;
    const radio = findDealBarRadio(dealBarId);
    if (!radio) return false;

    if (radio.checked && !force) {
      scheduleHeaderPriceMirror();
      return true;
    }

    urlSyncDisabled = true;
    if (radio.checked && force) {
      const otherRadio = queryAll(
        'input[type="radio"][name^="kaching-bundles-deal-"]:not(:checked)'
      )[0];
      if (otherRadio) {
        otherRadio.click();
        radio.click();
      } else {
        radio.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }
    } else {
      radio.click();
    }
    urlSyncDisabled = false;
    scheduleHeaderPriceMirror();
    return radio.checked;
  }

  function bindDealBarListeners() {
    document.querySelectorAll('kaching-bundles-block').forEach((block) => {
      if (block.dataset.enzoOfferListen === 'true') return;
      block.dataset.enzoOfferListen = 'true';

      block.addEventListener('deal-bar-selected', (event) => {
        const detail = event.detail || {};
        const offer = offerForDealBarId(detail.dealBarId);
        if (offer) setOfferInUrl(offer);
        scheduleHeaderPriceMirror();
      });
    });
  }

  function patchVariantPickerUrlAndPriceSync() {
    const variantSelects = customElements.get('variant-selects');
    if (!variantSelects || variantSelects.prototype.updateURL._enzoOfferPatch) return;

    variantSelects.prototype.updateURL = function () {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', String(this.currentVariant.id));

      const offer = getOfferFromUrl();
      if (offer) url.searchParams.set(OFFER_PARAM, String(offer));

      window.history.replaceState({}, '', url.toString());
    };
    variantSelects.prototype.updateURL._enzoOfferPatch = true;

    variantSelects.prototype.renderProductInfo = function () {
      if (!this.currentVariant) return;

      const sectionKey = this.dataset.originalSection
        ? this.dataset.originalSection
        : this.dataset.section;

      fetch(
        `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${sectionKey}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sectionId = this.dataset.section;
          const pairs = [
            [`price-${sectionKey}`, `price-${sectionId}`],
            [`price-${sectionKey}-mobile-bar`, `price-${sectionId}-mobile-bar`],
          ];
          pairs.forEach(([sourceId, destId]) => {
            const source = html.getElementById(sourceId);
            const destination = document.getElementById(destId);
            if (source && destination) destination.innerHTML = source.innerHTML;
          });

          const price = document.getElementById(`price-${this.dataset.section}`);
          if (price) price.classList.remove('visibility-hidden');

          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
          );

          scheduleNativePriceSyncAfterVariantChange();
        });
    };
    variantSelects.prototype.renderProductInfo._enzoOfferPatch = true;
  }

  function ensureVariantPickerPatch() {
    if (customElements.get('variant-selects')) {
      patchVariantPickerUrlAndPriceSync();
      return;
    }
    customElements.whenDefined('variant-selects').then(patchVariantPickerUrlAndPriceSync);
  }

  function applyOfferFromUrl(force) {
    const offer = getOfferFromUrl();
    patchKachingDealBlockSettings(offer);
    loadDealBars();
    bindDealBarListeners();
    markNativePriceElements();

    if (!offer) {
      scheduleHeaderPriceMirror();
      return;
    }

    if (offerFromUrlApplied && !force) {
      scheduleHeaderPriceMirror();
      return;
    }

    const dealBarId = dealBarIdForOffer(offer);
    if (dealBarId) {
      document.querySelectorAll('kaching-bundles-block').forEach((block) => {
        block.setAttribute('selected-deal-bar-id', dealBarId);
      });
      if (selectDealBarInDom(dealBarId) || getSelectedDealBarId() === dealBarId) {
        offerFromUrlApplied = true;
      }
    }
  }

  let kachingReadyDebounce = null;
  function onKachingBlocksReady() {
    if (kachingReadyHandled) return;
    if (!document.querySelector('kaching-bundles-block')) return;
    kachingReadyHandled = true;
    bindDealBarListeners();
    applyOfferFromUrl(false);
    startHeaderPriceMirrorObserver();
    startPriceItemSanitizerObserver();
  }

  function scheduleKachingReadyCheck() {
    window.clearTimeout(kachingReadyDebounce);
    kachingReadyDebounce = window.setTimeout(onKachingBlocksReady, 50);
  }

  function wrapKachingInitialize() {
    const runPatch = () => {
      patchKachingDealBlockSettings(getOfferFromUrl());
      scheduleKachingReadyCheck();
    };

    if (typeof window.kachingBundlesInitialize === 'function') {
      const original = window.kachingBundlesInitialize;
      window.kachingBundlesInitialize = function (...args) {
        runPatch();
        return original.apply(this, args);
      };
      return;
    }

    let stored;
    Object.defineProperty(window, 'kachingBundlesInitialize', {
      configurable: true,
      enumerable: true,
      get() {
        return stored;
      },
      set(fn) {
        stored = function (...args) {
          runPatch();
          return fn.apply(this, args);
        };
      },
    });
  }

  wrapKachingInitialize();
  ensureVariantPickerPatch();

  const kachingMountObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (
          node.matches('kaching-bundles-block, kaching-bundles-bar, kaching-bundle') ||
          node.querySelector('kaching-bundles-block, kaching-bundles-bar, kaching-bundle')
        ) {
          scheduleKachingReadyCheck();
        }
      });
    }
  });
  kachingMountObserver.observe(document.documentElement, { childList: true, subtree: true });

  function bootOfferDeepLink() {
    ensureVariantPickerPatch();
    patchKachingDealBlockSettings(getOfferFromUrl());
    scheduleKachingReadyCheck();
    scheduleHeaderPriceMirror();
    startPriceItemSanitizerObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootOfferDeepLink);
  } else {
    bootOfferDeepLink();
  }

  let attempts = 0;
  const retry = window.setInterval(() => {
    bindDealBarListeners();
    if (!kachingReadyHandled) scheduleKachingReadyCheck();
    mirrorHeaderPriceFromKaching();
    attempts += 1;
    if (attempts >= 20) window.clearInterval(retry);
  }, 500);
})();
